import { z } from 'zod'

/**
 * Project Page Validation Schemas
 *
 * Zod schemas for validating project page data on create/update operations.
 * Includes coercion for arrays and proper handling of optional fields.
 */

/**
 * Helper to coerce string arrays
 * Handles: comma-separated strings, arrays, null, undefined, empty strings
 * All empty-ish inputs become empty arrays
 */
const stringArraySchema = z
  .union([z.string(), z.array(z.string()), z.null(), z.undefined()])
  .transform((val): string[] => {
    if (!val) return []
    if (typeof val === 'string') {
      return val.split(',').map((s) => s.trim()).filter(Boolean)
    }
    if (Array.isArray(val)) {
      return val.filter(Boolean)
    }
    return []
  })
  .default([])

/**
 * Gallery image schema
 * Each image requires a valid URL and alt text for accessibility
 */
const galleryImageSchema = z.object({
  url: z.string().url('Must be a valid URL'),
  alt: z.string().min(1, 'Alt text is required for accessibility'),
  caption: z.string().optional().nullable().transform((val) => val?.trim() || undefined)
})

/**
 * Gallery images array schema
 * Handles null/undefined gracefully, defaults to empty array
 */
const galleryImagesSchema = z
  .array(galleryImageSchema)
  .optional()
  .nullable()
  .default([])

// Slug validation: lowercase, alphanumeric with hyphens
const slugSchema = z
  .string()
  .min(1, 'Slug is required')
  .max(200, 'Slug must be 200 characters or less')
  .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
  .refine((val) => !val.startsWith('-') && !val.endsWith('-'), {
    message: 'Slug cannot start or end with a hyphen'
  })

// Status enum
const projectPageStatusSchema = z.enum(['draft', 'published', 'archived'])

/**
 * URL validation (optional)
 * Accepts: valid URL, empty string, null, undefined
 * Empty values are transformed to undefined for clean database storage
 */
const urlSchema = z
  .string()
  .optional()
  .nullable()
  .transform((val) => {
    // Transform empty/null/undefined to undefined
    if (!val || val.trim() === '') return undefined
    return val.trim()
  })
  .pipe(
    z.union([
      z.string().url('Must be a valid URL (e.g., https://example.com)'),
      z.undefined()
    ])
  )

/**
 * Date validation (ISO 8601)
 * Accepts: ISO datetime string, empty string, null, undefined
 * Empty values are transformed to undefined for clean database storage
 */
const dateSchema = z
  .string()
  .optional()
  .nullable()
  .transform((val) => {
    // Transform empty/null/undefined to undefined
    if (!val || val.trim() === '') return undefined
    return val.trim()
  })
  .pipe(
    z.union([
      z.string().datetime({ message: 'Must be a valid ISO date-time' }),
      z.undefined()
    ])
  )

// Twitter card types
const twitterCardTypeSchema = z
  .enum(['summary', 'summary_large_image', 'app', 'player'])
  .optional()
  .default('summary_large_image')

// Open Graph types
const ogTypeSchema = z
  .enum(['website', 'article', 'profile'])
  .optional()
  .default('website')

/**
 * Main project page form schema
 */
export const projectPageFormSchema = z.object({
  // Required fields
  gallery_id: z.number().int().positive('Gallery is required'),
  slug: slugSchema,
  title: z.string().min(1, 'Title is required').max(200, 'Title must be 200 characters or less'),
  status: projectPageStatusSchema,

  // Optional core fields - handle null/empty values gracefully
  description: z.string().optional().nullable().transform((val) => val?.trim() || undefined),
  hero_image_url: urlSchema,

  // Gallery images (array of image objects with url, alt, caption)
  gallery_images: galleryImagesSchema,

  // Location - all optional, handle null values
  service_city: z.string().max(100).optional().nullable().transform((val) => val?.trim() || undefined),
  service_province: z.string().max(100).optional().nullable().transform((val) => val?.trim() || undefined),
  service_type: z.string().max(100).optional().nullable().transform((val) => val?.trim() || undefined),

  // SEO - all optional, handle null values
  seo_title: z.string().max(200).optional().nullable().transform((val) => val?.trim() || undefined),
  seo_description: z.string().max(500).optional().nullable().transform((val) => val?.trim() || undefined),
  keywords: stringArraySchema,
  canonical_url: urlSchema,
  schema_json: z.record(z.any()).optional().nullable(),

  // Open Graph - all optional, handle null values
  og_title: z.string().max(200).optional().nullable().transform((val) => val?.trim() || undefined),
  og_description: z.string().max(500).optional().nullable().transform((val) => val?.trim() || undefined),
  og_image_url: urlSchema,
  og_type: ogTypeSchema,

  // Twitter Card - all optional, handle null values
  twitter_card_type: twitterCardTypeSchema,
  twitter_title: z.string().max(200).optional().nullable().transform((val) => val?.trim() || undefined),
  twitter_description: z.string().max(500).optional().nullable().transform((val) => val?.trim() || undefined),
  twitter_image_url: urlSchema,

  // Publishing
  publish_at: dateSchema,
  featured: z.boolean().optional().default(false),
  sort_order: z.number().int().min(0).optional().default(0),
  tags: stringArraySchema,

  // Project Details - ALL OPTIONAL
  // These fields accept null/empty values gracefully
  completion_date: z
    .string()
    .optional()
    .nullable()
    .transform((val) => {
      // Transform empty/null/undefined to undefined
      if (!val || val.trim() === '') return undefined
      return val.trim()
    })
    .pipe(
      z.union([
        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Completion date must be in YYYY-MM-DD format'),
        z.undefined()
      ])
    ),
  materials_used: stringArraySchema,
  price_range: z
    .enum([
      '$ · Under $1,000',
      '$$ · $1,000 - $5,000',
      '$$$ · $5,000 - $10,000',
      '$$$$ · $10,000 - $20,000',
      '$$$$$ · $20,000+'
    ])
    .optional()
    .nullable()
    .transform((val) => val || undefined),
  testimonial_text: z.string().optional().nullable().transform((val) => val?.trim() || undefined),
  testimonial_author: z.string().max(200).optional().nullable().transform((val) => val?.trim() || undefined),
  related_project_ids: z
    .array(z.string().uuid())
    .optional()
    .nullable()
    .default([])
})

/**
 * Schema for slug uniqueness check payload
 */
export const slugCheckSchema = z.object({
  slug: slugSchema,
  excludeId: z.string().uuid().optional()
})

/**
 * Schema for bulk update operations
 */
export const bulkUpdateSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'At least one project page must be selected'),
  updates: z.object({
    status: projectPageStatusSchema.optional(),
    featured: z.boolean().optional(),
    tags: stringArraySchema.optional()
  })
})

/**
 * Schema for publishing operations
 */
export const publishSchema = z.object({
  id: z.string().uuid(),
  publish_at: dateSchema.optional()
})

/**
 * Utility: Generate slug from title
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special chars
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .substring(0, 200) // Enforce max length
}

/**
 * Utility: Check if slug is valid
 */
export function isValidSlug(slug: string): boolean {
  return slugSchema.safeParse(slug).success
}

/**
 * Type exports
 */
export type ProjectPageFormValues = z.infer<typeof projectPageFormSchema>
export type SlugCheckValues = z.infer<typeof slugCheckSchema>
export type BulkUpdateValues = z.infer<typeof bulkUpdateSchema>
export type PublishValues = z.infer<typeof publishSchema>
